// ─── Registry des formats de numéros par pays ────────────────────────────────
//
// Stratégie d'adaptation rapide face aux évolutions de plans de numérotation :
//
//   Niveau 1 — Cette registry compile-time (cf. PHONE_FORMATS ci-dessous).
//     Patcher = commit + redéploiement. Délai ~heures.
//
//   Niveau 2 — Override via la variable VITE_PHONE_FORMATS (build-time).
//     getPhoneFormat() merge la registry de base avec cet override.
//     Délai ~minutes.
//
//   Niveau 3 — Backend autoritaire (run-time).
//     Le serveur /payments/initiate valide le numéro et renvoie une erreur
//     explicite si invalide. Le frontend reste indulgent côté UX.
//     Délai = 0 (le backend déploie sa nouvelle règle, le frontend continue
//     à tourner sans redéploiement).
//
// ⚠️ Écart web : sur natif, le niveau 2 se patchait par OTA Expo Update sans
// rebuild. Ici un override implique de rebuilder et redéployer le bundle — mais
// comme il s'agit de fichiers statiques, l'opération reste de l'ordre de la
// minute, donc la stratégie à trois niveaux garde son sens.
//
// Sources de vérité officielles :
//   - ITU-T E.164 (préfixes internationaux)
//   - ARCEP Bénin (réforme 2021 : 8 → 10 digits, préfixe 01)
//   - ARTP Sénégal / autres autorités nationales

//
// ─── Depuis le 2026-09-06 : libphonenumber-js sous le capot ──────────────────
//
// Les huit pays ci-dessous restent CURATÉS, et pour une raison précise : ce sont
// eux qui définissent le `sendFormat`, c'est-à-dire ce que le backend et
// l'agrégateur reçoivent. Rien de ce que la librairie apporte ne doit changer
// cela.
//
// Ce qu'elle apporte, elle, c'est la COUVERTURE et la JUSTESSE :
//   • les 245 pays qu'elle connaît deviennent sélectionnables, avec leur
//     indicatif, leur drapeau et leur nom français ;
//   • la validation s'appuie sur les plans de numérotation réels plutôt que
//     sur un simple compte de chiffres.
//
// ⚠️ Elle ne REMPLACE PAS la règle de longueur historique, elle s'y AJOUTE.
// Vérifié le 2026-09-06 : `isValidPhoneNumber("97121234", "BJ")` rend FAUX —
// l'ancien plan béninois à 8 chiffres, que ce module tolère volontairement pour
// les numéros d'avant la réforme ARCEP. Faire confiance à la seule librairie
// aurait donc rejeté des numéros que l'app accepte aujourd'hui. Un numéro est
// valide si l'UNE des deux règles l'accepte : on ne peut qu'élargir.

import {
  AsYouType,
  getCountries,
  getCountryCallingCode,
  isValidPhoneNumber,
  type CountryCode as LibCountryCode,
} from "libphonenumber-js";

/** Tous les pays connus de la librairie. */
export type CountryCode = LibCountryCode;

/** Les pays dont le format d'envoi au backend est écrit à la main. */
export type CuratedCountry = "BJ" | "SN" | "CI" | "TG" | "ML" | "BF" | "GN" | "NE";

export type PhoneFormat = {
  countryCode:   CountryCode;
  flag:          string;            // emoji drapeau pour l'UI
  countryName:   string;            // libellé FR
  intlPrefix:    string;            // ex: "+229"
  // Validation : nombre de chiffres locaux attendus (après préfixe pays).
  // Tableau pour tolérer la transition (ex: Bénin 8 ou 10).
  localLengths:  number[];
  // Préfixe national obligatoire (ex: "01" pour Bénin post-2021).
  // null = pas de préfixe national.
  nationalPrefix: string | null;
  // Pattern d'affichage humain (utilise X = digit) : "XX XX XX XX XX".
  displayPattern: string;
  // Exemple à afficher en placeholder.
  example:       string;
  // Format final envoyé au backend. "national" = avec nationalPrefix.
  // "stripped" = sans nationalPrefix (compat anciennes API).
  // "e164" = avec intlPrefix.
  sendFormat:    "national" | "stripped" | "e164";
};

// ─── Registry compile-time ───────────────────────────────────────────────────
// À mettre à jour quand un pays change son plan de numérotation. La structure
// est conçue pour qu'un changement isolé n'impacte qu'une seule ligne.

const PHONE_FORMATS_BASE: Record<CuratedCountry, PhoneFormat> = {
  BJ: {
    countryCode:    "BJ",
    flag:           "🇧🇯",
    countryName:    "Bénin",
    intlPrefix:     "+229",
    // Réforme ARCEP 2021 : nouveau plan en 10 digits avec préfixe 01.
    // On tolère encore 8 digits pour la rétrocompat (numéros pré-2021).
    localLengths:   [10, 8],
    nationalPrefix: "01",
    displayPattern: "XX XX XX XX XX",
    example:        "01 97 12 34 56",
    sendFormat:     "national",
  },
  SN: {
    countryCode:    "SN",
    flag:           "🇸🇳",
    countryName:    "Sénégal",
    intlPrefix:     "+221",
    localLengths:   [9],
    nationalPrefix: null,
    displayPattern: "XX XXX XX XX",
    example:        "77 123 45 67",
    sendFormat:     "national",
  },
  CI: {
    countryCode:    "CI",
    flag:           "🇨🇮",
    countryName:    "Côte d'Ivoire",
    intlPrefix:     "+225",
    localLengths:   [10],
    nationalPrefix: "0",
    displayPattern: "XX XX XX XX XX",
    example:        "07 12 34 56 78",
    sendFormat:     "national",
  },
  TG: {
    countryCode:    "TG",
    flag:           "🇹🇬",
    countryName:    "Togo",
    intlPrefix:     "+228",
    localLengths:   [8],
    nationalPrefix: null,
    displayPattern: "XX XX XX XX",
    example:        "90 12 34 56",
    sendFormat:     "national",
  },
  ML: {
    countryCode:    "ML",
    flag:           "🇲🇱",
    countryName:    "Mali",
    intlPrefix:     "+223",
    localLengths:   [8],
    nationalPrefix: null,
    displayPattern: "XX XX XX XX",
    example:        "65 12 34 56",
    sendFormat:     "national",
  },
  BF: {
    countryCode:    "BF",
    flag:           "🇧🇫",
    countryName:    "Burkina Faso",
    intlPrefix:     "+226",
    localLengths:   [8],
    nationalPrefix: null,
    displayPattern: "XX XX XX XX",
    example:        "70 12 34 56",
    sendFormat:     "national",
  },
  GN: {
    countryCode:    "GN",
    flag:           "🇬🇳",
    countryName:    "Guinée",
    intlPrefix:     "+224",
    localLengths:   [9],
    nationalPrefix: null,
    displayPattern: "XXX XX XX XX",
    example:        "621 23 45 67",
    sendFormat:     "national",
  },
  NE: {
    countryCode:    "NE",
    flag:           "🇳🇪",
    countryName:    "Niger",
    intlPrefix:     "+227",
    localLengths:   [8],
    nationalPrefix: null,
    displayPattern: "XX XX XX XX",
    example:        "90 12 34 56",
    sendFormat:     "national",
  },
};

// ─── Override via variable d'environnement (niveau 2) ───────────────────────
// L'équipe peut corriger un format sans toucher au code : il suffit de
// redéployer avec la variable renseignée. Format attendu — du JSON dans
// web/.env :
//
//   VITE_PHONE_FORMATS={"BJ":{"localLengths":[10],"nationalPrefix":"01"}}
//
// L'override est merged par-dessus la registry de base.
type PhoneFormatsOverride = Partial<Record<CuratedCountry, Partial<PhoneFormat>>>;

function readOverrides(): PhoneFormatsOverride {
  const raw = import.meta.env.VITE_PHONE_FORMATS;
  if (!raw) return {};
  try {
    return JSON.parse(raw) as PhoneFormatsOverride;
  } catch {
    // JSON malformé : on ignore l'override plutôt que de casser le boot de
    // l'app entière pour une correction de format de téléphone.
    console.warn("[phone] VITE_PHONE_FORMATS ignoré — JSON invalide");
    return {};
  }
}

const PHONE_FORMATS: Record<CuratedCountry, PhoneFormat> = (() => {
  const overrides = readOverrides();
  const merged    = { ...PHONE_FORMATS_BASE };
  for (const k of Object.keys(merged) as CuratedCountry[]) {
    if (overrides[k]) merged[k] = { ...merged[k], ...overrides[k] };
  }
  return merged;
})();

// ─── API publique ────────────────────────────────────────────────────────────

/**
 * Le drapeau d'un pays, déduit de son code ISO.
 *
 * Les deux lettres deviennent des « indicateurs régionaux » Unicode, que le
 * système compose en drapeau. Écrire 245 emojis à la main aurait été 245
 * occasions de se tromper.
 */
function flagOf(code: string): string {
  return code
    .toUpperCase()
    .replace(/[A-Z]/g, (c) => String.fromCodePoint(0x1f1e6 + c.charCodeAt(0) - 65));
}

/** Le nom français d'un pays, rendu par le navigateur. */
const REGION_NAMES = (() => {
  try {
    return new Intl.DisplayNames(["fr"], { type: "region" });
  } catch {
    // Navigateur sans Intl.DisplayNames : on retombera sur le code ISO, ce qui
    // reste lisible dans une liste où le drapeau et l'indicatif sont là.
    return null;
  }
})();

function nameOf(code: CountryCode): string {
  try {
    return REGION_NAMES?.of(code) ?? code;
  } catch {
    return code;
  }
}

/**
 * Le format d'un pays NON curaté, déduit de la librairie.
 *
 * `sendFormat: "e164"` — hors UEMOA, aucun format national n'a été convenu avec
 * le backend. E.164 est la seule écriture non ambiguë d'un numéro étranger, et
 * c'est celle que tout agrégateur sait lire.
 */
function derivedFormat(country: CountryCode): PhoneFormat {
  return {
    countryCode: country,
    flag: flagOf(country),
    countryName: nameOf(country),
    intlPrefix: `+${getCountryCallingCode(country)}`,
    // Vide : la longueur attendue vient de la librairie, pas d'un compte écrit
    // à la main que personne ne tiendrait à jour pour 237 pays.
    localLengths: [],
    nationalPrefix: null,
    // Vide : l'affichage passe par `AsYouType`, qui connaît le découpage réel.
    displayPattern: "",
    example: "",
    sendFormat: "e164",
  };
}

export function getPhoneFormat(country: CountryCode): PhoneFormat {
  return PHONE_FORMATS[country as CuratedCountry] ?? derivedFormat(country);
}

/**
 * Les pays mis EN TÊTE de la liste — ceux que l'app sert vraiment.
 *
 * Ce ne sont plus les seuls sélectionnables : ce sont ceux qu'une Main ou un
 * client trouvera sans chercher.
 */
export const SUPPORTED_COUNTRIES = Object.keys(PHONE_FORMATS_BASE) as CountryCode[];

/** Les 245 pays de la librairie, hors ceux déjà mis en tête. */
export const ALL_COUNTRIES: CountryCode[] = getCountries();

export function isKnownCountry(code: string | null | undefined): code is CountryCode {
  return Boolean(code) && (ALL_COUNTRIES as string[]).includes(code as string);
}

// ─── Validation ──────────────────────────────────────────────────────────────
//
// Souple à dessein : le backend reste autoritaire. Si la validation passe ici,
// l'utilisateur peut soumettre. Si le backend rejette, on affiche son message.

export function validatePhone(format: PhoneFormat, raw: string): boolean {
  const digits = raw.replace(/\D/g, "");
  if (digits.length === 0) return false;

  // Strip d'un éventuel intl prefix laissé par l'utilisateur.
  const noIntl = stripIntlPrefix(digits, format);

  // 1) LE PLAN DE NUMÉROTATION RÉEL, via la librairie. C'est lui qui rend les
  //    237 pays non curatés utilisables, et qui rattrape les cas qu'un simple
  //    compte de chiffres laisse passer.
  try {
    if (isValidPhoneNumber(noIntl, format.countryCode)) return true;
  } catch {
    // Code pays inconnu de la librairie : on laisse la règle historique juger.
  }

  // 2) LA RÈGLE HISTORIQUE, conservée telle quelle.
  //
  //    ⚠️ Elle n'est pas redondante. `isValidPhoneNumber("97121234", "BJ")` rend
  //    FAUX : l'ancien plan béninois à 8 chiffres, d'avant la réforme ARCEP,
  //    que ce module tolère délibérément. S'en remettre à la seule librairie
  //    aurait rejeté des numéros que l'app accepte depuis toujours.
  if (format.localLengths.length === 0) return false;
  if (!format.localLengths.includes(noIntl.length)) return false;

  // Si un préfixe national est obligatoire, on le vérifie sur la longueur max.
  // (les longueurs intermédiaires tolérées sont des formats legacy.)
  const maxLen = Math.max(...format.localLengths);
  if (format.nationalPrefix && noIntl.length === maxLen) {
    if (!noIntl.startsWith(format.nationalPrefix)) return false;
  }

  return true;
}

// ─── Formatage pour affichage UI ──────────────────────────────────────────────

export function formatPhoneForDisplay(format: PhoneFormat, raw: string): string {
  const digits  = raw.replace(/\D/g, "");
  const noIntl  = stripIntlPrefix(digits, format);

  // Pays non curaté : la librairie connaît le découpage réel, on le lui laisse.
  // Les huit pays curatés gardent LEUR pattern — l'affichage du portefeuille et
  // de la course ne doit pas bouger sous les yeux des utilisateurs.
  if (!format.displayPattern) {
    try {
      return new AsYouType(format.countryCode).input(noIntl);
    } catch {
      return noIntl;
    }
  }

  // Applique le pattern (X = digit, autres chars conservés).
  let out  = "";
  let idx  = 0;
  for (const c of format.displayPattern) {
    if (c === "X") {
      if (idx >= noIntl.length) break;
      out += noIntl[idx++];
    } else if (idx > 0) {
      out += c;
    }
  }
  return out;
}

// ─── Normalisation pour envoi backend ────────────────────────────────────────

export function normalizeForBackend(format: PhoneFormat, raw: string): string {
  const digits = stripIntlPrefix(raw.replace(/\D/g, ""), format);

  switch (format.sendFormat) {
    case "national":
      return digits;
    case "stripped":
      return format.nationalPrefix && digits.startsWith(format.nationalPrefix)
        ? digits.slice(format.nationalPrefix.length)
        : digits;
    case "e164":
      // Avec le « + » : un numéro étranger sans indicatif explicite est
      // ambigu pour l'agrégateur. Aucun pays curaté n'utilise ce mode, donc ce
      // changement n'affecte rien de ce qui part aujourd'hui.
      return `${format.intlPrefix}${digits}`;
  }
}

function stripIntlPrefix(digits: string, format: PhoneFormat): string {
  const intl = format.intlPrefix.replace(/\D/g, "");
  return digits.startsWith(intl) ? digits.slice(intl.length) : digits;
}
